import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../lib/styleGuide';
import WelcomeStep from './onboarding/WelcomeStep';
import ShopIntroStep from './onboarding/ShopIntroStep';
import FoodTypeSelectionStep from './onboarding/FoodTypeSelectionStep';
import CottageFoodEligibilityStep from './onboarding/CottageFoodEligibilityStep';
import PerishableMealsRequirementsStep from './onboarding/PerishableMealsRequirementsStep';
import NotSureYetGuidanceStep from './onboarding/NotSureYetGuidanceStep';
import ShopNameStep from './onboarding/ShopNameStep';
import ShopLocationIntroStep from './onboarding/ShopLocationIntroStep';
import ShopLocationStep from './onboarding/ShopLocationStep';
import VisualsStoryStep from './onboarding/VisualsStoryStep';
import PaymentStep from './onboarding/PaymentStep';
import VerifyIdentityStep from './onboarding/VerifyIdentityStep';
import ReviewLaunchStep from './onboarding/ReviewLaunchStep';


import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const steps = [
  { label: 'Welcome', component: WelcomeStep },
  { label: 'Shop Intro', component: ShopIntroStep },
  { label: 'Food Type Selection', component: FoodTypeSelectionStep },
  { label: 'Cottage Food Eligibility', component: CottageFoodEligibilityStep, conditional: true },
  { label: 'Perishable Meals Requirements', component: PerishableMealsRequirementsStep, conditional: true },
  { label: 'Not Sure Yet Guidance', component: NotSureYetGuidanceStep, conditional: true },
  { label: 'Shop Name', component: ShopNameStep },
  { label: 'Shop Location Intro', component: ShopLocationIntroStep },
  { label: 'Location', component: ShopLocationStep },
  { label: 'Visuals & Story', component: VisualsStoryStep },
  { label: 'Payment', component: PaymentStep },
  { label: 'Verify Identity', component: VerifyIdentityStep },
  { label: 'Review & Launch', component: ReviewLaunchStep },
];

export default function OnboardingFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState({});
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [checkingStore, setCheckingStore] = useState(true);

  // Check if user already has a store - redirect to seller hub if they do
  useEffect(() => {
    const checkExistingStore = async () => {
      if (!currentUser?.uid) {
        setCheckingStore(false);
        return;
      }

      try {
        // Check if user already has a shop document
        const shopsRef = collection(db, 'shops');
        const q = query(shopsRef, where('sellerId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          navigate('/seller-hub');
          return;
        }
        
        setCheckingStore(false);
      } catch (error) {
        console.error('Error checking for existing store:', error);
        setCheckingStore(false);
      }
    };

    checkExistingStore();
  }, [currentUser, navigate]);

  // Fetch saved progress on mount
  useEffect(() => {
    // TODO: GET /shops/setup to restore progress
  }, []);

  // Show loading while checking for existing store
  if (checkingStore) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#FAFAFA'
      }}>
        <div style={{ 
          width: 32, 
          height: 32, 
          border: '3px solid transparent', 
          borderTop: '3px solid #C97B53', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }} />
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `
        }} />
      </div>
    );
  }

  // Determine which step to show, accounting for conditional steps
  const getCurrentStepComponent = () => {
    let stepIndex = currentStep;
    let step = steps[stepIndex];
    
    // If this is the Cottage Food Eligibility step, only show it if user selected shelf-stable
    if (step.component === CottageFoodEligibilityStep && progress.foodType !== 'shelf-stable') {
      stepIndex++;
      step = steps[stepIndex];
    }
    
    // If this is the Perishable Meals Requirements step, only show it if user selected perishable-meals or both
    if (step.component === PerishableMealsRequirementsStep && !['perishable-meals', 'both'].includes(progress.foodType)) {
      stepIndex++;
      step = steps[stepIndex];
    }
    
    // If this is the Not Sure Yet Guidance step, only show it if user selected not-sure
    if (step.component === NotSureYetGuidanceStep && progress.foodType !== 'not-sure') {
      stepIndex++;
      step = steps[stepIndex];
    }
    
    return step.component;
  };

  const StepComponent = getCurrentStepComponent();

  // Save progress after each step
  const saveProgress = (stepData) => {
    setProgress(prev => ({ ...prev, ...stepData }));
    // TODO: PATCH /shops/setup with current step data
  };

  // Payment completed successfully - no need to save to Firestore during onboarding
  const handleLaunchShop = async () => {
    if (!currentUser?.uid) return;
    
    // Payment has already been processed successfully by Stripe
    // Shop data can be saved later when the user actually sets up their shop
    
    // TODO: In the future, this could call a backend API to save shop data
    // For now, just mark the onboarding step as complete
  };

  const goNext = (stepData) => {
  if (stepData) {
    saveProgress(stepData);
  }
  
  // Check if we need to skip the next step (conditional step)
  const nextStepIndex = currentStep + 1;
  if (nextStepIndex < steps.length) {
    const nextStep = steps[nextStepIndex];
    
    // Skip Cottage Food Eligibility step if user didn't select shelf-stable
    if (nextStep.component === CottageFoodEligibilityStep && stepData?.foodType !== 'shelf-stable') {
      setCurrentStep(s => Math.min(s + 2, steps.length - 1));
    } 
    // Skip Perishable Meals Requirements step if user didn't select perishable-meals or both
    else if (nextStep.component === PerishableMealsRequirementsStep && !['perishable-meals', 'both'].includes(stepData?.foodType)) {
      setCurrentStep(s => Math.min(s + 2, steps.length - 1));
    }
    // Skip Not Sure Yet Guidance step if user didn't select not-sure
    else if (nextStep.component === NotSureYetGuidanceStep && stepData?.foodType !== 'not-sure') {
      setCurrentStep(s => Math.min(s + 2, steps.length - 1));
    } else {
      setCurrentStep(s => Math.min(s + 1, steps.length - 1));
    }
  } else {
    setCurrentStep(s => Math.min(s + 1, steps.length - 1));
  }
};
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 0));

  // Hide progress bar for welcome, intro, food type selection, cottage food eligibility, and shop name steps
  const showProgressBar = currentStep > 4;

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#F9F9F9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: 24 }}>

      
      {/* Progress Bar */}
      {showProgressBar && (
        <div style={{ width: '100%', maxWidth: 480, margin: '32px auto 24px auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {steps.slice(5).map((step, idx) => (
            <div key={step.label} style={{ flex: 1, height: 6, borderRadius: 3, background: idx < currentStep - 5 ? COLORS.PRIMARY_GRADIENT : '#E0CFC2', transition: 'background 0.3s' }} />
          ))}
        </div>
      )}
      {/* Step Content */}
      <div style={{ position: 'relative', width: '100%', maxWidth: 480, background: '#fff', borderRadius: 20, boxShadow: '0 2px 12px #0001', padding: 32, marginBottom: 32 }}>
        {/* Close Button */}
        <button
          onClick={() => navigate('/customer-dashboard')}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.7'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
          aria-label="Close onboarding and return to dashboard"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <StepComponent
          data={progress}
          onNext={currentStep === steps.length - 2 ? async (stepData) => { // On Review & Launch step
            saveProgress(stepData);
            await handleLaunchShop();
            setCurrentStep(s => Math.min(s + 1, steps.length - 1));
          } : goNext}
          onBack={goBack}
          isFirst={currentStep === 0}
          isLast={currentStep === steps.length - 1}
        />
      </div>
    </div>
  );
} 